import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Layers } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { Badge } from "../components/Badge";
import { ErrorPanel, Loading } from "../components/Loading";
import { EditableField } from "../components/ui/EditableField";
import { PageHeader } from "../components/ui/PageHeader";
import { api } from "../lib/api";

/**
 * Allowance detail page. Inline-edit on every mutable field plus a
 * "Used by" panel showing how many Grades, Positions, and Contracts
 * reference this allowance.
 */
export function AllowanceDetailPage() {
  const { t } = useTranslation();
  const { allowanceId } = useParams<{ allowanceId: string }>();
  const qc = useQueryClient();

  const allowQ = useQuery({
    queryKey: ["allowance", allowanceId],
    queryFn: () => api.getAllowance(allowanceId!),
    enabled: !!allowanceId,
  });

  const usageQ = useQuery({
    queryKey: ["allowance-usage", allowanceId],
    queryFn: () => api.getAllowanceUsage(allowanceId!),
    enabled: !!allowanceId,
  });

  if (allowQ.isLoading) return <Loading />;
  if (allowQ.error) return <ErrorPanel message={(allowQ.error as Error).message} />;
  if (!allowQ.data) return <ErrorPanel message={t("policies.allowance_not_found")} />;

  const a = allowQ.data;

  const patch = (patch: Record<string, unknown>) =>
    api.updateAllowance(a.id, patch).then((updated) => {
      qc.setQueryData(["allowance", a.id], updated);
      qc.invalidateQueries({ queryKey: ["allowances"] });
      return updated;
    });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`${a.code} · ${a.name_en}`}
        breadcrumbs={[
          { label: t("policies.allowance_back"), href: "/policies/allowances" },
          { label: a.code },
        ]}
        actions={
          !a.is_active ? (
            <Badge tone="slate">{t("policies.loan_products_inactive_badge")}</Badge>
          ) : undefined
        }
      />

      {/* Identity */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.allowance_section_identity")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("policies.name_en_label")}
            value={a.name_en}
            onSave={(v) => patch({ name_en: v })}
          />
          <EditableField
            label={t("policies.name_ar_label")}
            value={a.name_ar}
            rtl
            onSave={(v) => patch({ name_ar: v })}
          />
          <EditableField
            label={t("common.type")}
            value={a.allowance_type}
            kind="select"
            options={[
              { value: "allowance", label: t("policies.allowance_t_allowance") },
              { value: "deduction", label: t("policies.allowance_t_deduction") },
              { value: "cost", label: t("policies.allowance_t_cost") },
            ]}
            onSave={(v) => patch({ allowance_type: v })}
          />
          <EditableField
            label={t("policies.allowances_category_label")}
            value={a.category}
            kind="select"
            options={[
              { value: "housing", label: t("policies.allowance_cat_housing") },
              { value: "transport", label: t("policies.allowance_cat_transport") },
              { value: "communication", label: t("policies.allowance_cat_communication") },
              { value: "iqama", label: t("policies.allowance_cat_iqama") },
              { value: "medical", label: t("policies.allowance_cat_medical") },
              { value: "performance", label: t("policies.allowance_cat_performance") },
              { value: "other", label: t("policies.allowance_cat_other") },
            ]}
            onSave={(v) => patch({ category: v })}
          />
          <EditableField
            label={t("policies.loan_products_currency")}
            value={a.currency}
            onSave={(v) => patch({ currency: v })}
          />
          <EditableField
            label={t("policies.active_label")}
            value={a.is_active}
            kind="boolean"
            onSave={(v) => patch({ is_active: v })}
          />
        </div>
      </section>

      {/* Calculation */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.allowance_section_calculation")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("policies.allowance_calc_mode")}
            value={a.amount_calculation}
            kind="select"
            options={[
              { value: "fixed", label: t("policies.allowance_fixed_amount") },
              { value: "percent", label: t("policies.allowance_percent_of_base") },
              { value: "per_day", label: t("policies.allowance_per_day_long") },
              { value: "formula", label: t("policies.allowance_formula") },
            ]}
            onSave={(v) => patch({ amount_calculation: v })}
          />
          <EditableField
            label={t("policies.allowance_fixed_amount")}
            value={a.fixed_amount}
            kind="number"
            onSave={(v) => patch({ fixed_amount: v == null ? null : String(v) })}
          />
          <EditableField
            label={t("policies.allowances_percent_label")}
            value={a.percent}
            kind="number"
            onSave={(v) => patch({ percent: v == null ? null : String(v) })}
          />
          <EditableField
            label={t("policies.allowances_per_day_label")}
            value={a.per_day_amount}
            kind="number"
            onSave={(v) =>
              patch({ per_day_amount: v == null ? null : String(v) })
            }
          />
          <EditableField
            label={t("policies.allowances_formula_label")}
            value={a.formula_ref}
            onSave={(v) => patch({ formula_ref: v })}
          />
          <EditableField
            label={t("policies.allowance_fixed_monthly")}
            value={a.fixed_monthly_amount}
            kind="number"
            onSave={(v) =>
              patch({ fixed_monthly_amount: v == null ? null : String(v) })
            }
          />
          <EditableField
            label={t("policies.allowance_min")}
            value={a.minimum_amount}
            kind="number"
            onSave={(v) =>
              patch({ minimum_amount: v == null ? null : String(v) })
            }
          />
          <EditableField
            label={t("policies.allowance_max")}
            value={a.maximum_amount}
            kind="number"
            onSave={(v) =>
              patch({ maximum_amount: v == null ? null : String(v) })
            }
          />
        </div>
      </section>

      {/* Inclusion flags */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.allowance_section_inclusion")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("policies.allowance_inc_tax")}
            value={a.included_in_tax}
            kind="boolean"
            onSave={(v) => patch({ included_in_tax: v })}
          />
          <EditableField
            label={t("policies.allowance_inc_gosi")}
            value={a.included_in_gosi}
            kind="boolean"
            onSave={(v) => patch({ included_in_gosi: v })}
          />
          <EditableField
            label={t("policies.allowance_inc_eos")}
            value={a.included_in_eos_base}
            kind="boolean"
            onSave={(v) => patch({ included_in_eos_base: v })}
          />
          <EditableField
            label={t("policies.allowance_inc_sub_cycle")}
            value={a.included_in_sub_cycle}
            kind="boolean"
            onSave={(v) => patch({ included_in_sub_cycle: v })}
          />
        </div>
      </section>

      {/* Used by */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
          <Layers className="h-4 w-4" />
          {t("policies.allowance_usage_title")}
        </h2>
        {usageQ.isLoading ? (
          <Loading />
        ) : usageQ.data ? (
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {t("policies.allowance_usage_grades")}
              </div>
              <div className="mt-1 text-3xl font-bold tabular-nums">
                {usageQ.data.grade_count}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {t("policies.allowance_usage_positions")}
              </div>
              <div className="mt-1 text-3xl font-bold tabular-nums">
                {usageQ.data.position_count}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {t("policies.allowance_usage_contracts")}
              </div>
              <div className="mt-1 text-3xl font-bold tabular-nums">
                {usageQ.data.contract_count}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm italic text-slate-500">{t("policies.allowance_usage_unavailable")}</p>
        )}
      </section>
    </div>
  );
}
